import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSettlementStore } from '../../stores/settlementStore';

interface PartialPaymentModalProps {
  isVisible: boolean;
  onClose: () => void;
  settlement: {
    id: string;
    driver_id: string;
    driver_name: string;
    period_month: number;
    period_year: number;
    closing_balance: number;
    outstanding_balance: number;
  };
  onPaymentSuccess: () => void;
}

type PaymentMethod = 'cash' | 'bank_transfer' | 'check' | 'card' | 'online_transfer';

const PartialPaymentModal: React.FC<PartialPaymentModalProps> = ({
  isVisible,
  onClose,
  settlement,
  onPaymentSuccess,
}) => {
  const { recordPayment } = useSettlementStore();

  // Form state
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  // Available payment methods
  const paymentMethods: { value: PaymentMethod; label: string; icon: string }[] = [
    { value: 'cash', label: 'Cash', icon: 'cash-outline' },
    { value: 'bank_transfer', label: 'Bank Transfer', icon: 'card-outline' },
    { value: 'check', label: 'Check', icon: 'document-text-outline' },
    { value: 'card', label: 'Card', icon: 'card' },
    { value: 'online_transfer', label: 'Online', icon: 'phone-portrait-outline' },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const validateAmount = () => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return 'Please enter a valid amount greater than zero';
    }
    if (numAmount > settlement.outstanding_balance) {
      return `Amount cannot exceed outstanding balance (${formatCurrency(settlement.outstanding_balance)})`;
    }
    return null;
  };

  const handlePayment = async () => {
    const validation = validateAmount();
    if (validation) {
      Alert.alert('Invalid Amount', validation);
      return;
    }

    const numAmount = parseFloat(amount);
    const balanceAfter = settlement.outstanding_balance - numAmount;

    // Show confirmation
    Alert.alert(
      'Confirm Payment',
      `Record payment of ${formatCurrency(numAmount)} for ${settlement.driver_name}?\n\n` +
      `Outstanding Balance: ${formatCurrency(settlement.outstanding_balance)}\n` +
      `Payment: -${formatCurrency(numAmount)}\n` +
      `New Balance: ${formatCurrency(balanceAfter)}${balanceAfter === 0 ? ' ✅ FULLY PAID' : ''}`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Confirm', onPress: processPayment },
      ]
    );
  };

  const processPayment = async () => {
    try {
      setLoading(true);
      const numAmount = parseFloat(amount);

      await recordPayment(settlement.id, {
        payment_amount: numAmount,
        payment_method: paymentMethod as 'cash' | 'bank_transfer' | 'check' | 'offset' | 'other',
        payment_reference: paymentReference || undefined,
        payment_date: paymentDate,
        notes: notes || undefined,
      });

      const balanceAfter = settlement.outstanding_balance - numAmount;
      const isFullyPaid = balanceAfter === 0;

      Alert.alert(
        'Success',
        `Payment of ${formatCurrency(numAmount)} recorded successfully!\n\n` +
        `New Outstanding Balance: ${formatCurrency(balanceAfter)}` +
        `${isFullyPaid ? '\n\n🎉 Settlement fully paid!' : ''}`,
        [
          { 
            text: 'OK', 
            onPress: () => {
              resetForm();
              onPaymentSuccess();
              onClose();
            }
          }
        ]
      );
    } catch (error: any) {
      console.error('Payment error:', error);
      Alert.alert('Error', error.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAmount('');
    setPaymentMethod('cash');
    setPaymentReference('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setNotes('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const setFullAmount = () => {
    setAmount(settlement.outstanding_balance.toFixed(2));
  };

  if (!isVisible) return null;

  return (
    <View style={styles.overlay}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <View style={styles.modal}>
          {/* Header */}
          <View style={styles.header}>
            <View>
              <Text style={styles.title}>Record Payment</Text>
              <Text style={styles.subtitle}>
                {settlement.driver_name} - {settlement.period_month}/{settlement.period_year}
              </Text>
            </View>
            <TouchableOpacity onPress={handleClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color="#666" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Balance Info */}
            <View style={styles.balanceInfo}>
              <View style={styles.balanceRow}>
                <Text style={styles.balanceLabel}>Settlement Balance:</Text>
                <Text style={styles.balanceValue}>
                  {formatCurrency(settlement.closing_balance)}
                </Text>
              </View>
              <View style={styles.balanceRow}>
                <Text style={styles.balanceLabel}>Outstanding Balance:</Text>
                <Text style={[styles.balanceValue, styles.outstandingBalance]}>
                  {formatCurrency(settlement.outstanding_balance)}
                </Text>
              </View>
              {settlement.outstanding_balance !== settlement.closing_balance && (
                <View style={styles.infoBox}>
                  <Ionicons name="information-circle" size={16} color="#3498DB" />
                  <Text style={styles.infoText}>
                    Previous payments have been recorded
                  </Text>
                </View>
              )}
            </View>

            {/* Amount Input */}
            <View style={styles.inputGroup}>
              <View style={styles.labelRow}>
                <Text style={styles.label}>Payment Amount *</Text>
                <TouchableOpacity onPress={setFullAmount} style={styles.fullPayButton}>
                  <Text style={styles.fullPayText}>Full Amount</Text>
                </TouchableOpacity>
              </View>
              <View style={styles.amountInputContainer}>
                <Text style={styles.currencySymbol}>AED</Text>
                <TextInput
                  style={styles.amountInput}
                  value={amount}
                  onChangeText={setAmount}
                  placeholder="0.00"
                  keyboardType="decimal-pad"
                  returnKeyType="done"
                />
              </View>
              <Text style={styles.hint}>
                Maximum: {formatCurrency(settlement.outstanding_balance)}
              </Text>
            </View>

            {/* Payment Method */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Payment Method *</Text>
              <View style={styles.methodGrid}>
                {paymentMethods.map((method) => (
                  <TouchableOpacity
                    key={method.value}
                    style={[
                      styles.methodButton,
                      paymentMethod === method.value && styles.methodButtonActive
                    ]}
                    onPress={() => setPaymentMethod(method.value)}
                  >
                    <Ionicons 
                      name={method.icon as any} 
                      size={20} 
                      color={paymentMethod === method.value ? '#FFFFFF' : '#666'} 
                    />
                    <Text style={[
                      styles.methodText,
                      paymentMethod === method.value && styles.methodTextActive
                    ]}>
                      {method.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>

            {/* Payment Reference */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>
                Payment Reference {paymentMethod === 'cash' ? '(Optional)' : ''}
              </Text>
              <TextInput
                style={styles.input}
                value={paymentReference}
                onChangeText={setPaymentReference}
                placeholder={
                  paymentMethod === 'bank_transfer' ? 'Transaction ID or Reference' :
                  paymentMethod === 'check' ? 'Check Number' :
                  paymentMethod === 'card' ? 'Last 4 digits' :
                  paymentMethod === 'online_transfer' ? 'Transaction ID' :
                  'Reference (optional)'
                }
                returnKeyType="next"
              />
            </View>

            {/* Payment Date */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Payment Date *</Text>
              <TouchableOpacity style={styles.dateInput}>
                <Ionicons name="calendar-outline" size={20} color="#666" />
                <Text style={styles.dateText}>{formatDate(paymentDate)}</Text>
              </TouchableOpacity>
              <Text style={styles.hint}>
                Defaulted to today. Tap to change (feature coming soon)
              </Text>
            </View>

            {/* Notes */}
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Notes (Optional)</Text>
              <TextInput
                style={[styles.input, styles.textArea]}
                value={notes}
                onChangeText={setNotes}
                placeholder="Add any additional notes..."
                multiline
                numberOfLines={3}
                textAlignVertical="top"
                returnKeyType="done"
              />
            </View>

            {/* Preview */}
            {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
              <View style={styles.preview}>
                <View style={styles.previewHeader}>
                  <Ionicons name="calculator-outline" size={20} color="#2C3E50" />
                  <Text style={styles.previewTitle}>Payment Preview</Text>
                </View>
                
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>Outstanding Balance:</Text>
                  <Text style={styles.previewValue}>
                    {formatCurrency(settlement.outstanding_balance)}
                  </Text>
                </View>
                
                <View style={styles.previewRow}>
                  <Text style={styles.previewLabel}>Payment Amount:</Text>
                  <Text style={[styles.previewValue, styles.paymentAmount]}>
                    -{formatCurrency(parseFloat(amount))}
                  </Text>
                </View>
                
                <View style={styles.divider} />
                
                <View style={[styles.previewRow, styles.previewTotal]}>
                  <Text style={styles.previewTotalLabel}>New Balance:</Text>
                  <Text style={[
                    styles.previewTotalValue,
                    settlement.outstanding_balance - parseFloat(amount) === 0 && styles.fullyPaid
                  ]}>
                    {formatCurrency(settlement.outstanding_balance - parseFloat(amount))}
                  </Text>
                </View>
                
                {settlement.outstanding_balance - parseFloat(amount) === 0 && (
                  <View style={styles.successBanner}>
                    <Ionicons name="checkmark-circle" size={20} color="#27AE60" />
                    <Text style={styles.successText}>Settlement will be fully paid!</Text>
                  </View>
                )}
              </View>
            )}
          </ScrollView>

          {/* Action Buttons */}
          <View style={styles.footer}>
            <TouchableOpacity
              style={[styles.button, styles.cancelButton]}
              onPress={handleClose}
              disabled={loading}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[
                styles.button, 
                styles.submitButton,
                (loading || !amount || validateAmount() !== null) && styles.submitButtonDisabled
              ]}
              onPress={handlePayment}
              disabled={loading || !amount || validateAmount() !== null}
            >
              {loading ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>Record Payment</Text>
              )}
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  container: {
    width: '90%',
    maxWidth: 500,
    maxHeight: '90%',
  },
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    maxHeight: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E5E5',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: '#7F8C8D',
  },
  closeButton: {
    padding: 4,
  },
  content: {
    flex: 1,
    padding: 20,
  },
  balanceInfo: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  balanceLabel: {
    fontSize: 14,
    color: '#7F8C8D',
  },
  balanceValue: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
  },
  outstandingBalance: {
    color: '#E74C3C',
    fontSize: 18,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#EBF5FB',
    padding: 8,
    borderRadius: 6,
    marginTop: 8,
  },
  infoText: {
    fontSize: 12,
    color: '#3498DB',
    marginLeft: 6,
    flex: 1,
  },
  inputGroup: {
    marginBottom: 20,
  },
  labelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 16,
    fontWeight: '500',
    color: '#2C3E50',
  },
  fullPayButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#3498DB',
    borderRadius: 4,
  },
  fullPayText: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
  },
  amountInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 2,
    borderColor: '#4CAF50',
    borderRadius: 8,
    backgroundColor: '#FFFFFF',
    paddingHorizontal: 16,
  },
  currencySymbol: {
    fontSize: 18,
    fontWeight: '600',
    color: '#7F8C8D',
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    padding: 16,
    fontSize: 24,
    fontWeight: '700',
    color: '#2C3E50',
  },
  hint: {
    fontSize: 12,
    color: '#95A5A6',
    marginTop: 4,
  },
  methodGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  methodButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#DDD',
    backgroundColor: '#FFFFFF',
    gap: 6,
  },
  methodButtonActive: {
    backgroundColor: '#4CAF50',
    borderColor: '#4CAF50',
  },
  methodText: {
    fontSize: 13,
    color: '#666',
    fontWeight: '500',
  },
  methodTextActive: {
    color: '#FFFFFF',
  },
  input: {
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#FFFFFF',
    color: '#2C3E50',
  },
  textArea: {
    minHeight: 80,
    paddingTop: 12,
  },
  dateInput: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#DDD',
    borderRadius: 8,
    padding: 12,
    backgroundColor: '#F8F9FA',
    gap: 8,
  },
  dateText: {
    fontSize: 16,
    color: '#2C3E50',
  },
  preview: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 8,
    marginTop: 10,
    borderWidth: 1,
    borderColor: '#E5E5E5',
  },
  previewHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  previewTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
  },
  previewRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  previewLabel: {
    fontSize: 14,
    color: '#7F8C8D',
  },
  previewValue: {
    fontSize: 14,
    fontWeight: '500',
    color: '#2C3E50',
  },
  paymentAmount: {
    color: '#E74C3C',
  },
  divider: {
    height: 1,
    backgroundColor: '#DDD',
    marginVertical: 8,
  },
  previewTotal: {
    marginBottom: 0,
  },
  previewTotalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
  },
  previewTotalValue: {
    fontSize: 18,
    fontWeight: '700',
    color: '#E74C3C',
  },
  fullyPaid: {
    color: '#27AE60',
  },
  successBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#D5F4E6',
    padding: 10,
    borderRadius: 6,
    marginTop: 12,
    gap: 8,
  },
  successText: {
    fontSize: 14,
    color: '#27AE60',
    fontWeight: '600',
  },
  footer: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
    gap: 12,
  },
  button: {
    flex: 1,
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelButton: {
    backgroundColor: '#95A5A6',
  },
  cancelButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  submitButton: {
    backgroundColor: '#4CAF50',
  },
  submitButtonDisabled: {
    backgroundColor: '#BDC3C7',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default PartialPaymentModal;
